import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatIconButton } from "@angular/material/button";
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from "@angular/cdk/overlay";
import { ColorPickerDirective } from "ngx-color-picker";

/**
 * Generic color-picker trigger + inline panel.
 *
 * - **Dot trigger** (default): shows a small colored circle; clicking it opens
 *   the inline panel beneath it.
 * - **Icon trigger**: pass a Material icon name via `triggerIcon` (e.g. `"palette"`)
 *   to replace the dot with a `mat-icon` button.
 * - **Hidden trigger**: pass `[triggerHidden]="true"` to suppress the trigger
 *   entirely and always render the panel inline — useful when an external element
 *   (e.g. a toolbar button + `cdkConnectedOverlay`) already controls visibility.
 *
 * The component is stateless from the caller's perspective: the caller provides
 * `color`, reacts to `colorChange` (every change) / `colorApply` (Save) /
 * `colorCancel` (Restore Default), and never needs to manage the open state.
 */
@Component({
  selector: "lib-color-swatch-picker",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatIconButton, ColorPickerDirective, CdkConnectedOverlay, CdkOverlayOrigin, NgTemplateOutlet],
  templateUrl: "./color-swatch-picker.component.html",
  styleUrl: "./color-swatch-picker.component.scss",
})
export class ColorSwatchPickerComponent {
  /** Current color — sets the dot fill and the initial panel value on open. */
  readonly color = input.required<string>();

  /**
   * Material icon name for the trigger button (e.g. `"palette"`).
   * When omitted the trigger shows a filled dot using the `color` input.
   */
  readonly triggerIcon = input<string | undefined>(undefined);

  /** Dot size in px (only relevant when `triggerIcon` is not set). */
  readonly size = input(12);

  /**
   * When `true`, hides the trigger button and always renders the panel.
   * Use this when the panel is shown/hidden by an external mechanism such as
   * a `cdkConnectedOverlay`.
   */
  readonly triggerHidden = input(false);

  /** Alpha channel mode passed to ngx-color-picker. */
  readonly alphaChannel = input<"disabled" | "forced" | "always">("disabled");

  /** Color output format passed to ngx-color-picker. */
  readonly outputFormat = input<"hex" | "rgba">("hex");

  /** Preset color swatches shown at the bottom of the picker panel. */
  readonly presetColors = input<string[]>([]);

  /** ARIA label for the trigger button. */
  readonly ariaLabel = input("Change color");

  /** Fires on every color change while the panel is open (real-time preview). */
  readonly colorChange = output<string>();

  /** Fires with the confirmed color when the user clicks "Save". */
  readonly colorApply = output<string>();

  /** Fires when the user clicks "Restore Default". */
  readonly colorCancel = output<void>();

  /** Whether the panel is open (only meaningful when `triggerHidden` is false). */
  readonly open = signal(false);

  /** In-flight value while the user interacts with the panel. */
  protected readonly pendingColor = signal("");

  // ElementRef generic is erased at runtime — nativeElement typed as any; cast at call site.
  private readonly elementRef = inject(ElementRef);

  /**
   * Reference to the CDK overlay directive so we can force a reposition once
   * ngx-color-picker has rendered its content (its final size isn't known on
   * the first attach, so the initial position calculation may pick "below"
   * even when only "above" fits).
   */
  private readonly overlay = viewChild(CdkConnectedOverlay);

  /**
   * Preferred positions for the floating overlay panel (tried in order).
   * Only relevant when `triggerHidden` is `false`.
   */
  readonly overlayPositions: ConnectedPosition[] = [
    { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top" },
    { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom" },
    { originX: "end", originY: "bottom", overlayX: "end", overlayY: "top" },
    { originX: "end", originY: "top", overlayX: "end", overlayY: "bottom" },
  ];

  constructor() {
    // Sync pendingColor from `color` whenever the panel becomes visible:
    // - trigger mode  (`triggerHidden=false`): sync on open() transition
    // - hidden-trigger (`triggerHidden=true`):  panel is always rendered, so sync
    //   on every color() change (safe because signal equality short-circuits when
    //   the user's own in-flight pick sets pendingColor to the same value)
    effect(() => {
      if (this.open() || this.triggerHidden()) {
        this.pendingColor.set(this.color());
      }
    });
  }

  protected toggle(): void {
    if (!this.open()) {
      this.pendingColor.set(this.color());
    }
    this.open.update((v) => !v);
  }

  /** Programmatically open the panel. */
  openPicker(): void {
    this.pendingColor.set(this.color());
    this.open.set(true);
  }

  /** Programmatically close the panel. */
  close(): void {
    this.open.set(false);
  }

  protected handleApply(color: string): void {
    this.colorApply.emit(color);
    this.open.set(false);
  }

  protected handleCancel(): void {
    this.colorCancel.emit();
    this.open.set(false);
  }

  /**
   * Patches the ngx-color-picker hex `<input>` so it:
   * - Is limited to 7 characters (`#` + 6 hex digits)
   * - Always starts with `#`
   * - Strips non-hex characters on every keystroke
   *
   * Called via `(colorPickerOpen)` which fires once per panel instantiation
   * (ngx-color-picker fires this event on init when `cpToggle=true`).
   * A `data-hex-patched` attribute guards against duplicate listeners when
   * the overlay re-renders.
   *
   * Search order: CDK overlay panes first (component's own overlay or a parent
   * overlay), then the component's host element (direct inline rendering).
   */
  protected fixHexInput(): void {
    // Re-run overlay positioning now that the picker's real content is in the DOM.
    // Without this, CDK measures the panel while it's still ~empty and may lock in
    // the "below" position even when the swatch is near the viewport bottom.
    this.overlay()?.overlayRef?.updatePosition();

    setTimeout(() => {
      // Second pass after the browser has laid out the picker — catches the case
      // where colorPickerOpen fires before the panel's final size is measurable.
      this.overlay()?.overlayRef?.updatePosition();

      // Cast nativeElement (any) to Element so querySelectorAll<T> is properly typed.
      const host = this.elementRef.nativeElement as Element;
      const inOverlays = Array.from(
        document.querySelectorAll<HTMLInputElement>(".cdk-overlay-pane .hex-text input"),
      )[0];
      const inHost = Array.from(host.querySelectorAll<HTMLInputElement>(".hex-text input"))[0];
      // Last candidate = most recently opened picker. Renamed to avoid no-shadow with "input" event.
      const hexInput: HTMLInputElement | undefined = [inOverlays, inHost].filter((item) => !!item).at(-1);

      if (!hexInput || hexInput.hasAttribute("data-hex-patched")) return;
      hexInput.setAttribute("data-hex-patched", "1");

      hexInput.maxLength = 7;
      if (!hexInput.value.startsWith("#")) {
        hexInput.value = "#" + hexInput.value.replace(/#/g, "");
      }

      hexInput.addEventListener("input", () => {
        let v = hexInput.value.replace(/[^0-9A-Fa-f#]/g, "");
        v = "#" + v.replace(/#/g, "");
        hexInput.value = v.slice(0, 7);
      });
    });
  }
}
